import { createTheme, alpha } from '@mui/material/styles';

// Brand palette. These values are fixed and must not drift.
const PRIMARY = { light: '#e8f4fe', main: '#1976d2', dark: '#1565c0', contrastText: '#ffffff' };
const SECONDARY = { light: '#f3f0f9', main: '#5c35a5', dark: '#482880', contrastText: '#ffffff' };

// Fluid type: scales continuously between mobile and desktop instead of jumping at breakpoints.
const fluid = (minRem, maxRem, minVw = 360, maxVw = 1440) => {
  const slope = (maxRem - minRem) / (maxVw - minVw);
  const intercept = minRem - slope * minVw;
  return `clamp(${minRem}rem, ${intercept.toFixed(4)}rem + ${(slope * 100).toFixed(4)}vw, ${maxRem}rem)`;
};

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: PRIMARY,
    secondary: SECONDARY,
    success: { light: '#e8f5e9', main: '#43a047', dark: '#2e7d32' },
    error: { light: '#ffebee', main: '#d32f2f', dark: '#c62828' },
    warning: { light: '#fff8e1', main: '#ffd740', dark: '#f9a825' },
    background: { default: '#ffffff', paper: '#ffffff' },
    text: { primary: '#212121', secondary: '#757575' }
  },
  typography: {
    fontFamily: '"Inter", "Roboto", "Helvetica", "Arial", sans-serif',
    // Tabular figures keep stat counters from shifting width as they animate.
    h1: { fontSize: fluid(2.25, 3.75), fontWeight: 800, lineHeight: 1.08, letterSpacing: '-0.03em' },
    h2: { fontSize: fluid(1.875, 2.75), fontWeight: 800, lineHeight: 1.15, letterSpacing: '-0.025em' },
    h3: { fontSize: fluid(1.625, 2.25), fontWeight: 700, lineHeight: 1.2, letterSpacing: '-0.02em' },
    h4: { fontSize: fluid(1.375, 1.75), fontWeight: 700, lineHeight: 1.3, letterSpacing: '-0.015em' },
    h5: { fontSize: fluid(1.175, 1.5), fontWeight: 700, lineHeight: 1.4, letterSpacing: '-0.01em' },
    h6: { fontSize: fluid(1.05, 1.25), fontWeight: 700, lineHeight: 1.45, letterSpacing: '-0.005em' },
    body1: { fontSize: '1rem', lineHeight: 1.7 },
    body2: { fontSize: '0.875rem', lineHeight: 1.6 },
    overline: { fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase' },
    button: { fontWeight: 600, letterSpacing: '0.01em' }
  },
  shape: { borderRadius: 12 },
  shadows: [
    'none',
    '0px 2px 4px rgba(0, 0, 0, 0.05)',
    '0px 4px 8px rgba(0, 0, 0, 0.08)',
    '0px 8px 16px rgba(0, 0, 0, 0.1)',
    '0px 12px 24px rgba(0, 0, 0, 0.12)',
    '0px 16px 32px rgba(0, 0, 0, 0.15)',
    '0px 20px 40px rgba(0, 0, 0, 0.18)',
    '0px 24px 48px rgba(0, 0, 0, 0.2)',
    '0px 32px 64px rgba(0, 0, 0, 0.25)',
    ...Array(16).fill('0px 32px 64px rgba(0, 0, 0, 0.25)')
  ],
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        html: { WebkitFontSmoothing: 'antialiased', MozOsxFontSmoothing: 'grayscale', textSizeAdjust: '100%' },
        body: {
          overflowX: 'hidden',
          fontFeatureSettings: '"cv02", "cv03", "cv04", "cv11"'
        },
        // Native anchor scrolling; disabled for users who ask for reduced motion.
        '@media (prefers-reduced-motion: no-preference)': {
          html: { scrollBehavior: 'smooth' }
        },
        '@media (prefers-reduced-motion: reduce)': {
          '*, *::before, *::after': {
            animationDuration: '0.01ms !important',
            animationIterationCount: '1 !important',
            transitionDuration: '0.01ms !important',
            scrollBehavior: 'auto !important'
          }
        },
        // A single visible focus ring for every interactive element.
        ':focus-visible': {
          outline: `3px solid ${PRIMARY.main}`,
          outlineOffset: 2,
          borderRadius: 4
        },
        '::selection': { backgroundColor: alpha(PRIMARY.main, 0.2) },
        img: { maxWidth: '100%' }
      }
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: {
          textTransform: 'none',
          borderRadius: 12,
          padding: '12px 24px',
          fontSize: '1rem',
          fontWeight: 600,
          boxShadow: 'none',
          minHeight: 44, // WCAG 2.2 target size
          transition: 'transform 0.2s ease, box-shadow 0.2s ease, background 0.2s ease',
          '&:focus-visible': { outline: `3px solid ${PRIMARY.main}`, outlineOffset: 2 }
        },
        contained: {
          background: `linear-gradient(135deg, ${PRIMARY.main} 0%, ${PRIMARY.dark} 100%)`,
          '&:hover': {
            background: `linear-gradient(135deg, ${PRIMARY.dark} 0%, #0d47a1 100%)`,
            boxShadow: `0 8px 24px ${alpha(PRIMARY.main, 0.35)}`
          }
        },
        outlined: { borderWidth: 2, '&:hover': { borderWidth: 2 } }
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          '&:focus-visible': { outline: `3px solid ${PRIMARY.main}`, outlineOffset: 2 }
        }
      }
    },
    MuiLink: {
      defaultProps: { underline: 'hover' },
      styleOverrides: {
        root: { '&:focus-visible': { outline: `3px solid ${PRIMARY.main}`, outlineOffset: 2, borderRadius: 4 } }
      }
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 16,
          boxShadow: `0px 4px 20px ${alpha('#000', 0.06)}`,
          border: `1px solid ${alpha('#000', 0.05)}`,
          transition: 'box-shadow 0.3s ease, transform 0.3s ease, border-color 0.3s ease'
        }
      }
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } }
    },
    MuiAppBar: {
      styleOverrides: {
        root: {
          backgroundColor: alpha('#ffffff', 0.72),
          backdropFilter: 'blur(20px) saturate(180%)',
          WebkitBackdropFilter: 'blur(20px) saturate(180%)',
          borderBottom: `1px solid ${alpha('#000', 0.05)}`,
          boxShadow: 'none',
          color: '#212121'
        }
      }
    },
    MuiContainer: {
      styleOverrides: {
        root: { paddingLeft: 20, paddingRight: 20, '@media (min-width:600px)': { paddingLeft: 24, paddingRight: 24 } }
      }
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: { fontSize: '0.8rem', borderRadius: 8, padding: '8px 12px', backgroundColor: alpha('#212121', 0.94) }
      }
    }
  }
});

/**
 * Futuristic surface tokens, derived from the brand palette so nothing here
 * introduces a new hue. Consumed via theme.surfaces.* in components.
 */
theme.surfaces = {
  // Layered radial "aurora" wash used behind full-bleed sections.
  aurora: `radial-gradient(60rem 40rem at 12% -10%, ${alpha(PRIMARY.main, 0.14)} 0%, transparent 60%),
           radial-gradient(50rem 35rem at 88% 8%, ${alpha(SECONDARY.main, 0.12)} 0%, transparent 60%),
           radial-gradient(45rem 30rem at 50% 108%, ${alpha(PRIMARY.main, 0.08)} 0%, transparent 60%)`,
  // Faint technical grid; pairs with aurora for depth without noise.
  grid: `linear-gradient(${alpha(PRIMARY.main, 0.07)} 1px, transparent 1px),
         linear-gradient(90deg, ${alpha(PRIMARY.main, 0.07)} 1px, transparent 1px)`,
  gridSize: '64px 64px',
  glass: {
    background: `linear-gradient(135deg, ${alpha('#fff', 0.86)} 0%, ${alpha('#fff', 0.7)} 100%)`,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${alpha('#fff', 0.6)}`
  },
  glassDark: {
    background: `linear-gradient(135deg, ${alpha('#fff', 0.16)} 0%, ${alpha('#fff', 0.06)} 100%)`,
    backdropFilter: 'blur(20px) saturate(180%)',
    WebkitBackdropFilter: 'blur(20px) saturate(180%)',
    border: `1px solid ${alpha('#fff', 0.22)}`
  },
  glow: (color, strength = 0.35) => `0 0 0 1px ${alpha(color, 0.12)}, 0 18px 48px ${alpha(color, strength)}`
};

export default theme;
